import React from 'react'
import { useReadContract, useReadContracts, useChainId, useAccount } from 'wagmi'
import { getNFTContract } from '@/config/contract'
import { defaultChainId, isSupportedNetwork } from '@/config/wagmi'
import { formatEther } from 'viem'
import { formatEthDisplay } from '@/lib/utils'

export interface ContractInfo {
  currentPrice: string
  currentPriceFormatted: string
  totalMinted: bigint
  maxSupply: bigint
  whitelistActive: boolean
  revealed: boolean
  isPaused: boolean
  maxPerWallet: bigint
  maxPerTx: bigint
  startPrice: string
  endPrice: string
  auctionStartTime: bigint
  progress: number
}

/**
 * Hook to fetch and aggregate contract information
 * Uses useReadContracts for efficient multi-call
 */
export const useContractInfo = () => {
  const { isConnected } = useAccount()
  const chainIdFromWallet = useChainId()

  /**
   * IMPORTANT:
   * - When wallet is NOT connected we must NOT use wagmi's default chain (31337 / hardhat),
   *   otherwise production dapp will try to read from `http://127.0.0.1:8545` and all
   *   contract data will be zero/undefined on mobile.
   * - For read‑only queries we always fall back to `defaultChainId` unless
   *   the user is connected AND the wallet network is one of the supported chains.
   */
  const chainId = isConnected && isSupportedNetwork(chainIdFromWallet || 0)
    ? (chainIdFromWallet as number)
    : defaultChainId

  const contract = getNFTContract(chainId)
  
  const { data, isLoading, isError, error, refetch } = useReadContracts({
    contracts: [
      { ...contract, functionName: 'getCurrentPrice' },
      { ...contract, functionName: 'totalMinted' },
      { ...contract, functionName: 'MAX_SUPPLY' },
      { ...contract, functionName: 'whitelistActive' },
      { ...contract, functionName: 'revealed' },
      { ...contract, functionName: 'paused' },
      { ...contract, functionName: 'MAX_PER_WALLET' },
      { ...contract, functionName: 'MAX_PER_TX' },
      { ...contract, functionName: 'startPrice' },
      { ...contract, functionName: 'endPrice' },
      { ...contract, functionName: 'auctionStartTime' },
    ],
    query: {
      enabled: !!contract.address && contract.address !== '0x',
      retry: 3,
      retryDelay: 1000,
      refetchInterval: 30000,
    },
  })

  // No verbose debug logging here to keep console clean for real errors.

  // Debug error logging
  if (isError && error && typeof window !== 'undefined') {
    console.error('❌ useContractInfo error:', {
      error,
      chainId,
      contractAddress: contract.address,
      errorMessage: error?.message,
      errorDetails: error,
    })
    
    // Show helpful error message
    if (error?.message?.includes('could not be found') || error?.message?.includes('does not exist')) {
      console.error('💡 Contract not found at address:', contract.address)
      console.error('💡 Make sure:')
      console.error('   1. Hardhat node is running (npx hardhat node)')
      console.error('   2. Contract is deployed to this address')
      console.error('   3. You are connected to the correct network')
    }
  }
  
  if (isLoading || isError || !data) {
    return {
      data: null,
      isLoading,
      isError,
      error,
      refetch,
    }
  }

  // Parse the multicall results
  const [
    currentPrice,
    totalMinted,
    maxSupply,
    whitelistActive,
    revealed,
    isPaused,
    maxPerWallet,
    maxPerTx,
    startPrice,
    endPrice,
    auctionStartTime,
  ] = data

  // Calculate progress
  const progress =
    maxSupply.result && totalMinted.result
      ? (Number(totalMinted.result) / Number(maxSupply.result)) * 100
      : 0

  const contractInfo: ContractInfo = {
    currentPrice: currentPrice.result?.toString() || '0',
    currentPriceFormatted: currentPrice.result
      ? formatEthDisplay(formatEther(currentPrice.result))
      : '0',
    totalMinted: totalMinted.result || 0n,
    maxSupply: maxSupply.result || 0n,
    whitelistActive: whitelistActive.result || false,
    revealed: revealed.result || false,
    isPaused: isPaused.result || false,
    maxPerWallet: maxPerWallet.result || 0n,
    maxPerTx: maxPerTx.result || 0n,
    startPrice: startPrice.result ? formatEthDisplay(formatEther(startPrice.result)) : '0',
    endPrice: endPrice.result ? formatEthDisplay(formatEther(endPrice.result)) : '0',
    auctionStartTime: auctionStartTime.result || 0n,
    progress,
  }

  return {
    data: contractInfo,
    isLoading: false,
    isError: false,
    refetch,
  }
}

/**
 * Hook to get user-specific minting information
 */
export const useUserMintInfo = (address: `0x${string}` | undefined) => {
  const chainIdFromWallet = useChainId()
  const chainId = chainIdFromWallet || defaultChainId
  const contract = getNFTContract(chainId)
  
  const { data: numberMinted } = useReadContract({
    ...contract,
    functionName: 'numberMinted',
    args: address ? [address] : undefined,
    query: {
      enabled: !!address,
      refetchInterval: 30000,
    },
  })

  const { data: balance } = useReadContract({
    ...contract,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    query: {
      enabled: !!address,
      refetchInterval: 30000,
    },
  })

  return {
    numberMinted: numberMinted || 0n,
    balance: balance || 0n,
  }
}
